import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, Share2, RefreshCw, ExternalLink, Copy, Image, Globe } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface OpenGraphData {
  url: string;
  title: string;
  description: string;
  image: string;
  siteName: string;
  type: string;
  locale: string;
  twitterCard: string;
  twitterSite: string;
  twitterCreator: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
}

const OpenGraphPreviewTool: React.FC = () => {
  const [ogData, setOgData] = useState<OpenGraphData>({
    url: '',
    title: '',
    description: '',
    image: '',
    siteName: '',
    type: 'website',
    locale: 'en_US',
    twitterCard: 'summary_large_image',
    twitterSite: '',
    twitterCreator: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState<'facebook' | 'twitter' | 'linkedin'>('facebook');

  useSEO({
    title: 'Open Graph Preview Tool - Test Social Media Previews',
    description: 'Preview how your web pages will appear when shared on Facebook, Twitter, LinkedIn. Test Open Graph and Twitter Card meta tags.',
    keywords: 'open graph preview, social media preview, facebook preview, twitter card preview, linkedin preview, og tags',
    canonical: 'https://snap-tools.online/seo/open-graph-preview'
  });

  // Auto-fill Twitter data from Open Graph
  const autoFillTwitter = () => {
    setOgData(prev => ({
      ...prev,
      twitterTitle: prev.twitterTitle || prev.title,
      twitterDescription: prev.twitterDescription || prev.description,
      twitterImage: prev.twitterImage || prev.image
    }));
    toast.success('Twitter Card data auto-filled from Open Graph');
  };

  // Load example data
  const loadExample = () => {
    setOgData({
      url: 'https://example.com/amazing-product',
      title: 'Amazing Product - Revolutionary Solution for Modern Businesses',
      description: 'Discover our groundbreaking product that transforms how businesses operate. Increase efficiency by 300% with our innovative solution trusted by over 10,000 companies worldwide.',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=630&fit=crop',
      siteName: 'Example Company',
      type: 'website',
      locale: 'en_US',
      twitterCard: 'summary_large_image',
      twitterSite: '@example',
      twitterCreator: '@johnsmith',
      twitterTitle: 'Amazing Product - Revolutionary Business Solution',
      twitterDescription: 'Transform your business with our innovative solution. Trusted by 10,000+ companies. Try it free today!',
      twitterImage: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=630&fit=crop'
    });
    toast.success('Example data loaded');
  };

  // Fetch Open Graph data from URL
  const fetchOGData = async () => {
    if (!ogData.url) {
      toast.error('Please enter a URL to fetch');
      return;
    }

    setIsLoading(true);
    try {
      // In a real implementation, you would use a CORS proxy or backend service
      // For demo purposes, we'll simulate the process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock fetched data
      const mockData = {
        title: 'Fetched Page Title - Example Site',
        description: 'This is a fetched description from the webpage meta tags that would be extracted from the actual URL.',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=630&fit=crop',
        siteName: 'Example Website',
        type: 'article'
      };

      setOgData(prev => ({
        ...prev,
        ...mockData,
        twitterTitle: mockData.title,
        twitterDescription: mockData.description,
        twitterImage: mockData.image
      }));

      toast.success('Open Graph data fetched successfully');
    } catch (error) {
      console.error('Error fetching OG data:', error);
      toast.error('Failed to fetch Open Graph data');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate meta tags
  const generateMetaTags = () => {
    let tags = '';

    // Open Graph tags
    tags += '<!-- Open Graph -->\n';
    if (ogData.title) tags += `<meta property="og:title" content="${ogData.title}" />\n`;
    if (ogData.description) tags += `<meta property="og:description" content="${ogData.description}" />\n`;
    if (ogData.image) tags += `<meta property="og:image" content="${ogData.image}" />\n`;
    if (ogData.url) tags += `<meta property="og:url" content="${ogData.url}" />\n`;
    if (ogData.type) tags += `<meta property="og:type" content="${ogData.type}" />\n`;
    if (ogData.siteName) tags += `<meta property="og:site_name" content="${ogData.siteName}" />\n`;
    if (ogData.locale) tags += `<meta property="og:locale" content="${ogData.locale}" />\n`;

    // Twitter Card tags
    tags += '\n<!-- Twitter Card -->\n';
    if (ogData.twitterCard) tags += `<meta name="twitter:card" content="${ogData.twitterCard}" />\n`;
    if (ogData.twitterSite) tags += `<meta name="twitter:site" content="${ogData.twitterSite}" />\n`;
    if (ogData.twitterCreator) tags += `<meta name="twitter:creator" content="${ogData.twitterCreator}" />\n`;
    if (ogData.twitterTitle) tags += `<meta name="twitter:title" content="${ogData.twitterTitle}" />\n`;
    if (ogData.twitterDescription) tags += `<meta name="twitter:description" content="${ogData.twitterDescription}" />\n`;
    if (ogData.twitterImage) tags += `<meta name="twitter:image" content="${ogData.twitterImage}" />\n`;

    return tags;
  };

  // Copy meta tags to clipboard
  const copyMetaTags = async () => {
    const tags = generateMetaTags();
    try {
      await navigator.clipboard.writeText(tags);
      toast.success('Meta tags copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy meta tags');
    }
  };

  // Platform previews
  const FacebookPreview = () => (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white max-w-lg">
      {ogData.image && (
        <div className="aspect-video bg-gray-200 relative overflow-hidden">
          <img
            src={ogData.image}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}
      <div className="p-3">
        <div className="text-xs text-gray-500 uppercase mb-1">
          {ogData.url ? new URL(ogData.url).hostname : 'example.com'}
        </div>
        <h3 className="font-semibold text-gray-900 text-lg leading-tight mb-1">
          {ogData.title || 'Page Title'}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-2">
          {ogData.description || 'Page description will appear here...'}
        </p>
      </div>
    </div>
  );

  const TwitterPreview = () => (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white max-w-lg">
      {ogData.twitterImage && (
        <div className="aspect-video bg-gray-200 relative overflow-hidden">
          <img
            src={ogData.twitterImage}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}
      <div className="p-3">
        <h3 className="font-semibold text-gray-900 text-base leading-tight mb-1">
          {ogData.twitterTitle || ogData.title || 'Page Title'}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-2 mb-2">
          {ogData.twitterDescription || ogData.description || 'Page description will appear here...'}
        </p>
        <div className="text-xs text-gray-500">
          {ogData.url ? new URL(ogData.url).hostname : 'example.com'}
        </div>
      </div>
    </div>
  );

  const LinkedInPreview = () => (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white max-w-lg">
      {ogData.image && (
        <div className="aspect-video bg-gray-200 relative overflow-hidden">
          <img
            src={ogData.image}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 text-lg leading-tight mb-2">
          {ogData.title || 'Page Title'}
        </h3>
        <div className="text-xs text-gray-500 mb-2">
          {ogData.siteName || (ogData.url ? new URL(ogData.url).hostname : 'example.com')}
        </div>
        <p className="text-gray-600 text-sm line-clamp-3">
          {ogData.description || 'Page description will appear here...'}
        </p>
      </div>
    </div>
  );

  const previewComponents = {
    facebook: FacebookPreview,
    twitter: TwitterPreview,
    linkedin: LinkedInPreview
  };

  const PreviewComponent = previewComponents[previewPlatform];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Eye className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Open Graph Preview Tool</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Preview how your web pages will appear when shared on social media platforms. Test Open Graph and Twitter Card meta tags to optimize social sharing.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Quick Actions */}
          <div className="flex gap-3 justify-center mb-8 flex-wrap">
            <button
              onClick={loadExample}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Globe className="w-4 h-4" />
              Load Example
            </button>
            <button
              onClick={fetchOGData}
              disabled={isLoading}
              className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Fetch from URL
            </button>
            <button
              onClick={autoFillTwitter}
              className="flex items-center gap-2 bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Auto-fill Twitter
            </button>
            <button
              onClick={copyMetaTags}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy Meta Tags
            </button>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Input Form */}
            <div className="space-y-6">
              {/* URL Input */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">URL & Basic Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Page URL
                    </label>
                    <input
                      type="url"
                      value={ogData.url}
                      onChange={(e) => setOgData(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://example.com/page"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site Name
                    </label>
                    <input
                      type="text"
                      value={ogData.siteName}
                      onChange={(e) => setOgData(prev => ({ ...prev, siteName: e.target.value }))}
                      placeholder="Your Website Name"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OG Type
                      </label>
                      <select
                        value={ogData.type}
                        onChange={(e) => setOgData(prev => ({ ...prev, type: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="website">Website</option>
                        <option value="article">Article</option>
                        <option value="product">Product</option>
                        <option value="profile">Profile</option>
                        <option value="video">Video</option>
                        <option value="music">Music</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Locale
                      </label>
                      <select
                        value={ogData.locale}
                        onChange={(e) => setOgData(prev => ({ ...prev, locale: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="en_US">English (US)</option>
                        <option value="en_GB">English (UK)</option>
                        <option value="es_ES">Spanish</option>
                        <option value="fr_FR">French</option>
                        <option value="de_DE">German</option>
                        <option value="it_IT">Italian</option>
                        <option value="pt_BR">Portuguese (Brazil)</option>
                        <option value="ja_JP">Japanese</option>
                        <option value="ko_KR">Korean</option>
                        <option value="zh_CN">Chinese (Simplified)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Open Graph Data */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Open Graph Content</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={ogData.title}
                      onChange={(e) => setOgData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Compelling page title"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      maxLength={60}
                    />
                    <div className="text-xs text-gray-500 mt-1">{ogData.title.length}/60 characters</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={ogData.description}
                      onChange={(e) => setOgData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Engaging description that makes people want to click"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={3}
                      maxLength={160}
                    />
                    <div className="text-xs text-gray-500 mt-1">{ogData.description.length}/160 characters</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image URL
                    </label>
                    <input
                      type="url"
                      value={ogData.image}
                      onChange={(e) => setOgData(prev => ({ ...prev, image: e.target.value }))}
                      placeholder="https://example.com/image.jpg (1200x630px recommended)"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Twitter Card Data */}
              <div className="bg-cyan-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-cyan-900 mb-4">Twitter Card</h3>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Card Type
                      </label>
                      <select
                        value={ogData.twitterCard}
                        onChange={(e) => setOgData(prev => ({ ...prev, twitterCard: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="summary">Summary</option>
                        <option value="summary_large_image">Summary Large Image</option>
                        <option value="app">App</option>
                        <option value="player">Player</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Twitter Site
                      </label>
                      <input
                        type="text"
                        value={ogData.twitterSite}
                        onChange={(e) => setOgData(prev => ({ ...prev, twitterSite: e.target.value }))}
                        placeholder="@username"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Creator
                    </label>
                    <input
                      type="text"
                      value={ogData.twitterCreator}
                      onChange={(e) => setOgData(prev => ({ ...prev, twitterCreator: e.target.value }))}
                      placeholder="@author"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Title
                    </label>
                    <input
                      type="text"
                      value={ogData.twitterTitle}
                      onChange={(e) => setOgData(prev => ({ ...prev, twitterTitle: e.target.value }))}
                      placeholder="Twitter-specific title (optional)"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Description
                    </label>
                    <textarea
                      value={ogData.twitterDescription}
                      onChange={(e) => setOgData(prev => ({ ...prev, twitterDescription: e.target.value }))}
                      placeholder="Twitter-specific description (optional)"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={2}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Image
                    </label>
                    <input
                      type="url"
                      value={ogData.twitterImage}
                      onChange={(e) => setOgData(prev => ({ ...prev, twitterImage: e.target.value }))}
                      placeholder="Twitter-specific image URL (optional)"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-6">
              {/* Platform Selector */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Media Preview</h3>
                
                <div className="flex bg-white rounded-lg p-1 mb-6">
                  {[
                    { id: 'facebook', label: 'Facebook', color: 'bg-blue-600' },
                    { id: 'twitter', label: 'Twitter', color: 'bg-sky-500' },
                    { id: 'linkedin', label: 'LinkedIn', color: 'bg-blue-700' }
                  ].map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => setPreviewPlatform(platform.id as any)}
                      className={`flex-1 py-2 px-4 rounded-md transition-colors ${
                        previewPlatform === platform.id 
                          ? `${platform.color} text-white` 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {platform.label}
                    </button>
                  ))}
                </div>

                {/* Preview Display */}
                <div className="flex justify-center">
                  <PreviewComponent />
                </div>

                {/* Preview Info */}
                <div className="mt-6 bg-white rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Preview Information</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div><strong>Platform:</strong> {previewPlatform.charAt(0).toUpperCase() + previewPlatform.slice(1)}</div>
                    <div><strong>Image Dimensions:</strong> {
                      previewPlatform === 'twitter' ? '1200x628px' : '1200x630px'
                    }</div>
                    <div><strong>Title Length:</strong> {
                      previewPlatform === 'twitter' 
                        ? (ogData.twitterTitle || ogData.title).length 
                        : ogData.title.length
                    } characters</div>
                    <div><strong>Description Length:</strong> {
                      previewPlatform === 'twitter' 
                        ? (ogData.twitterDescription || ogData.description).length 
                        : ogData.description.length
                    } characters</div>
                  </div>
                </div>
              </div>

              {/* Generated Meta Tags */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Generated Meta Tags</h3>
                  <button
                    onClick={copyMetaTags}
                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                
                <div className="bg-white rounded-lg border p-4 h-64 overflow-y-auto">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                    {generateMetaTags()}
                  </pre>
                </div>
              </div>

              {/* Validation Tips */}
              <div className="bg-yellow-50 rounded-lg p-6">
                <h4 className="font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Optimization Tips
                </h4>
                <ul className="text-sm text-yellow-800 space-y-2">
                  <li>• <strong>Image Size:</strong> Use 1200x630px for best results</li>
                  <li>• <strong>Title Length:</strong> Keep under 60 characters</li>
                  <li>• <strong>Description:</strong> 150-160 characters optimal</li>
                  <li>• <strong>Image Format:</strong> JPG or PNG, under 1MB</li>
                  <li>• <strong>Test URLs:</strong> Use platform debugging tools</li>
                  <li>• <strong>Mobile Preview:</strong> Check on mobile devices</li>
                </ul>
              </div>

              {/* Debugging Tools */}
              <div className="bg-green-50 rounded-lg p-6">
                <h4 className="font-semibold text-green-900 mb-3">Debugging Tools</h4>
                <div className="space-y-3">
                  <a
                    href="https://developers.facebook.com/tools/debug/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-700 hover:text-green-800"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Facebook Sharing Debugger
                  </a>
                  <a
                    href="https://cards-dev.twitter.com/validator"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-700 hover:text-green-800"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Twitter Card Validator
                  </a>
                  <a
                    href="https://www.linkedin.com/post-inspector/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-700 hover:text-green-800"
                  >
                    <ExternalLink className="w-4 h-4" />
                    LinkedIn Post Inspector
                  </a>
                </div>
              </div>
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Open Graph Best Practices</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Compelling Titles:</strong> Create engaging, descriptive titles</li>
                <li>• <strong>Clear Descriptions:</strong> Write concise, action-oriented descriptions</li>
                <li>• <strong>High-Quality Images:</strong> Use visually appealing, relevant images</li>
                <li>• <strong>Consistent Branding:</strong> Maintain brand consistency across platforms</li>
                <li>• <strong>Test Regularly:</strong> Use platform debugging tools frequently</li>
                <li>• <strong>Monitor Performance:</strong> Track click-through rates from social media</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">Platform Differences</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• <strong>Facebook:</strong> Prefers 1200x630px images, shows full descriptions</li>
                <li>• <strong>Twitter:</strong> Uses Twitter Card format, shorter text</li>
                <li>• <strong>LinkedIn:</strong> Business-focused, professional imagery works best</li>
                <li>• <strong>WhatsApp:</strong> Uses Open Graph data for link previews</li>
                <li>• <strong>Slack:</strong> Shows rich previews using Open Graph</li>
                <li>• <strong>Discord:</strong> Displays embedded previews with images</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OpenGraphPreviewTool;
